/**
 * ReHub SEO website plans. The database functions in
 * supabase/migrations/20261022140000_seo_credit_plans.sql are the charge
 * authority. These figures must match that migration.
 */

export const SEO_TRIAL_DAYS = 30;
export const SEO_TRIAL_KEYWORDS = 5;

export const SEO_PLANS = [
  { key: 'starter', label: 'Starter', keywords: 10, monthlyCredits: 5, highlighted: false },
  { key: 'standard', label: 'Standard', keywords: 25, monthlyCredits: 10, highlighted: true },
  { key: 'growth', label: 'Growth', keywords: 50, monthlyCredits: 18, highlighted: false },
  { key: 'pro', label: 'Pro', keywords: 100, monthlyCredits: 30, highlighted: false },
] as const;

export type SeoPlanKey = (typeof SEO_PLANS)[number]['key'];

export const SEO_ACTION_CREDITS = {
  backlink_report: 5,
  competitor_analysis: 5,
} as const;

export type SeoPaidAction = keyof typeof SEO_ACTION_CREDITS;

const PLAN_BY_KEY = new Map(SEO_PLANS.map((plan) => [plan.key, plan]));

export function seoPlan(key: string): (typeof SEO_PLANS)[number] | null {
  return PLAN_BY_KEY.get(key as SeoPlanKey) ?? null;
}

/** Next calendar anniversary strictly after `after`. Day 31 uses the month's last day, then returns to the 31st. */
export function seoNextAnniversary(anchorDay: number, after: Date): Date {
  if (!Number.isInteger(anchorDay) || anchorDay < 1 || anchorDay > 31) throw new Error('invalid_anchor');
  const cursor = new Date(Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), 1));
  for (let i = 0; i < 36; i++) {
    const last = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0)).getUTCDate();
    const candidate = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), Math.min(anchorDay, last)));
    if (candidate.getTime() > after.getTime()) return candidate;
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  throw new Error('anniversary_not_found');
}

/** Whole credits, rounded up, for the rest of the current period. Zero when the new plan is not more expensive or the period has ended. */
export function seoProratedUpgradeCredits(currentCredits: number, nextCredits: number, periodStart: Date, periodEnd: Date, today: Date): number {
  const difference = nextCredits - currentCredits;
  if (difference <= 0) return 0;
  const span = utcDay(periodEnd) - utcDay(periodStart);
  const left = utcDay(periodEnd) - utcDay(today);
  if (span <= 0 || left <= 0) return 0;
  return Math.ceil((difference * left) / span);
}

function utcDay(value: Date): number {
  return Math.floor(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()) / 86_400_000);
}
